import React, { useEffect, useCallback } from "react";
import { Column, Text, Icon, Button } from "@amzn/meridian";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { COLORS } from "src/constants/colors";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import StepRenderer from "./StepRenderer";
import LeaveInspectionSheet from "./LeaveInspectionSheet";
import { setCurrentStep, setCurrentIterateStep, setIterationProgress, advanceStep, resetWorkflowPosition } from "src/store/workflowSlice";
import { resetInspectionCaptureState, setScannedBarcode } from "src/store/inspectionCaptureSlice";
import redoTokens from "@amzn/meridian-tokens/base/icon/redo"
import { useDualStreamCapture } from "src/components/pages/inspection/hooks/useDualStreamCapture";
import { WorkflowStep, IterateStep, InspectionQuestion } from "./types/inspectionWorkflow";
import { CameraStreamProps } from "./types/cameraStreamProps";
import { inspectionFlowTimingLogger, InspectionPhase } from "src/services/inspectionFlowTimingLogger";
import { useState } from "react";

/**
 * Maps the active step (or iterate sub-step) to one of the four timed
 * inspection phases plus the question id it maps to, mirroring the
 * mode/iterable mapping already used by austinInspectionCheckpointsPage.tsx's
 * buildPhases(). Returns phase null for steps that aren't one of the four
 * (read_barcode, get_drum_count, auto_calculated) or when no step is active.
 *
 * questionId (used as a metric dimension) is:
 *  - null for cage_walk (per-container loop, not a single question)
 *  - the step's question id for area_scan / photo_evidence / manual
 *
 * Each Area Scan question is its own manual_capture_multiple step with a
 * single-element question_ids list, so area_scan advances one question at a
 * time (photo -> submit -> next) and each step gets its own per-question
 * segment, exactly like photo_evidence / manual.
 */
function resolvePhase(
  step: WorkflowStep | IterateStep | null,
  currentIterateStep: WorkflowStep | null,
  questions: InspectionQuestion[]
): { phase: InspectionPhase | null; questionId: string | null } {
  // Inside the open-ended container loop (IterateStep): the whole loop,
  // including its read_barcode sub-step, is Cage Walk (no single question).
  if (step && "iterate" in step) {
    return { phase: "cage_walk", questionId: null };
  }
  const activeStep: any = currentIterateStep ?? step;
  if (!activeStep) {
    return { phase: null, questionId: null };
  }
  if (activeStep.type === "manual_capture_multiple") {
    // One question per area_scan step — take its single question id.
    const questionId: string | null = activeStep.backend?.question_ids?.[0] ?? null;
    return { phase: "area_scan", questionId };
  }
  if (activeStep.question_id) {
    const question = questions.find((q) => q.id === activeStep.question_id);
    if (question?.mode === "image") return { phase: "photo_evidence", questionId: activeStep.question_id };
    if (question?.mode === "manual") return { phase: "manual", questionId: activeStep.question_id };
  }
  return { phase: null, questionId: null };
}

/**
 * Returns true when the active step (or active iterate sub-step) requires camera access.
 * For iterate steps, checks `currentIterateStep` (the active sub-step from Redux),
 */
function needsCamera(
  step: WorkflowStep | IterateStep | null,
  currentIterateStep: WorkflowStep | null
): boolean {
  if (currentIterateStep) {
    return 'type' in currentIterateStep && currentIterateStep.type === 'detect_object';
  }
  if (step && 'type' in step) {
    return step.type === 'detect_object';
  }
  return false;
}

const WorkflowExecutor = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    activeWorkflow,
    showWorkflowHeader,
    currentStep,
    currentIterateStep,
    currentIterationIndex,
    currentCategoryIndex,
    workflowComplete,
    inspectionId,
    resumePending,
    resumeCompletedContainerCount,
    questions,
  } = useAppSelector((state) => state.workflow);
  const { resolutionScale } = useAppSelector((state) => state.inspectionCapture.streamingConfig);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const { flags } = useAppSelector((state) => state.featureFlags);


  useEffect(() => {
    if (!activeWorkflow) return;
    if (resumePending) {
      // TODO: `total` is vestigial. Its only display consumer
      // (ContainerCountOverlay) is dead code, and its only live consumer
      // (cameraFrame) uses `total > 0` purely as an "are we iterating?" guard
      // on a telemetry field. That guard can be derived from `currentStep`
      // ("iterate" in currentStep), letting us drop `total` from
      // setIterationProgress and `totalIterations` from the workflow slice.
      dispatch(setIterationProgress({
        currentIndex: resumeCompletedContainerCount,
        total: resumeCompletedContainerCount + 1,
      }));
      // A resumed draft is NOT phase-timed: we can't know how long the
      // inspector already spent in the current phase before leaving, so any
      // segment measured from the resume point would be a misleading partial.
      // Phase timing is only done for a fresh "Start Inspection" run. (Review
      // and submission timing still work — the review page identifies the
      // inspection on its own.)
      return;
    }
    dispatch(resetWorkflowPosition());
    // Reset iteration progress so a new inspection doesn't inherit stale
    // container counts from a previously run/left inspection.
    dispatch(setIterationProgress({ currentIndex: 0, total: 0 }));
  }, [activeWorkflow?.template_id, resumePending, resumeCompletedContainerCount, dispatch]);

  // Shared camera streams — lifecycle controlled by step type
  const {
    lowResVideoRef, highResVideoRef,
    captureHighResFrame,
    streamsReady,
    startStreams, stopStreams,
  } = useDualStreamCapture(resolutionScale, 1.0);

  // Start/stop camera streams based on whether the active step needs camera
  useEffect(() => {
    const controller = new AbortController();

    if (needsCamera(currentStep, currentIterateStep)) {
      startStreams(controller.signal);
    } else {
      stopStreams();
    }

    return () => {
      controller.abort();
    }
  }, [currentStep, currentIterateStep, startStreams, stopStreams]);

  // Navigate to the review page when the workflow completes
  useEffect(() => {
    if (workflowComplete && inspectionId) {
      navigate(`/configurationDrivenInspections/${inspectionId}/review`);
    }
  }, [workflowComplete, inspectionId, navigate]);

  // Keep the flow timer's active step (phase + question) in sync with
  // whichever step/sub-step is on screen. A new per-question segment starts
  // whenever the phase or question id changes. No-op when this inspection
  // wasn't started via startInspection() (a resumed draft) — see
  // resolvePhase / setActiveStep.
  useEffect(() => {
    const { phase, questionId } = resolvePhase(currentStep, currentIterateStep, questions);
    inspectionFlowTimingLogger.setActiveStep(phase, questionId);
  }, [currentStep, currentIterateStep, questions]);

  const cameraStreamProps: CameraStreamProps = {
    lowResVideoRef,
    highResVideoRef,
    captureHighResFrame,
    streamsReady,
  };

  if (!activeWorkflow) {
    return (
      <Column height="100vh" alignmentVertical="center" alignmentHorizontal="center">
        <Text type="h200">No active workflow</Text>
        <Button type="icon" onClick={() => navigate("/configurationDrivenInspections")}>
          <Icon tokens={redoTokens} color="primary" />
        </Button>
      </Column>
    );
  }

  const currentCategory = activeWorkflow.categories[currentCategoryIndex];

  const handleStepComplete = () => {
    dispatch(advanceStep());
  };

  // True while we're inside the open-ended container loop (the iterate step).
  const isContainerLoop = currentStep !== null && "iterate" in currentStep;

  // Leave the inspection and return to the dashboard / template list.
  const performLeave = () => {
    // Abandoned mid-inspection: the draft is still resumable server-side, but
    // this timing run has no valid "completion" to report. Discard rather
    // than let a stale run bleed into whatever inspection starts next.
    inspectionFlowTimingLogger.cancel();
    dispatch(resetInspectionCaptureState());
    dispatch(setCurrentStep(null));
    dispatch(setCurrentIterateStep(null));
    dispatch(setIterationProgress({ currentIndex: 0, total: 0 }));
    navigate("/home");
  };

  // Back button: while inside the container loop, confirm before leaving so
  // in-progress work isn't lost by accident. Otherwise leave immediately.
  const handleBack = () => {
    if (isContainerLoop) {
      setShowLeaveConfirm(true);
    } else {
      performLeave();
    }
  };

  // Ends the open-ended container loop and advances past the iterate step
  // (to the next category, or the review page when nothing remains).
  const handleFinishContainers = () => {
    dispatch(setScannedBarcode(null));
    dispatch(setCurrentIterateStep(null));
    handleStepComplete();
  };

  // Hide the header entirely for non-visual steps (e.g. auto_calculated)
  const isNonVisualStep = currentStep !== null && 'type' in currentStep && currentStep.type === 'auto_calculated';

  const headerText = isContainerLoop
    ? `Container ${currentIterationIndex + 1}`
    : currentCategory.human_text;

  return (
    <Column
      className="workflow-executor-viewport pwa-fullscreen-viewport"
      height="100vh"
      backgroundColor={COLORS.VERY_DARK_GRAY}
      alignmentVertical="justify"
    >
      {/* Header */}
      {showWorkflowHeader && !isNonVisualStep && <Column
        width="100%"
        spacingInset="500 300"
        className="inspection-info-wrapper"
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "30px",
              top: 0,
              bottom: 0,
              display: "flex",
              cursor: "pointer",
            }}
            onClick={handleBack}
          >
            <Icon tokens={arrowLeftTokens} color="inverted" />
          </div>
          <Text color="inverted" type="h300" alignment="center">{headerText}</Text>
        </div>
      </Column>}


      {currentStep !== null && <StepRenderer
        step={currentStep}
        onStepComplete={handleStepComplete}
        cameraStreamProps={cameraStreamProps}
        onFinish={isContainerLoop ? handleFinishContainers : undefined}
      />}

      <LeaveInspectionSheet
        open={showLeaveConfirm}
        onLeave={performLeave}
        onStay={() => setShowLeaveConfirm(false)}
      />

    </Column>
  );
};

export default WorkflowExecutor;
